<template>
  <div class="dashboard-calendar-card rounded-lg overflow-hidden">
    <div class="dashboard-calendar-card__header px-4 py-2">
      <span class="font-weight-medium text-gray-700">
        {{ DASHBOARD_CALENDAR_SECTION_TITLE }}
      </span>
    </div>

    <div class="bg-white rounded-lg">
      <div
        class="dashboard-calendar-card__body d-flex flex-column flex-md-row ga-3 pa-3"
      >
        <div class="flex-shrink-0">
          <ReminderCalendar
            embedded
            :markedDateKeys="markedDateKeys"
            :selectedDateKey="selectedDateKey"
            :mobile="mobile"
            :hasBanner="false"
            @update:selected-date-key="onSelectedDateKeyChange"
            @update:view-month="emit('update:viewMonth', $event)"
          />
        </div>

        <div class="bg-blue-gray-50 rounded-lg pa-2 flex-grow-1 min-w-0">
          <div
            class="py-4 d-flex align-center justify-center flex-wrap ga-2 mb-3 border-b-primary"
          >
            <span class="text-gray-600">{{ selectedWeekdayLabel }}</span>
            <span class="text-primary text-lg font-weight-bold">
              {{ selectedDayLabel }}
            </span>
            <span class="text-gray-600">{{ selectedMonthLabel }}</span>

            <span
              v-if="isSelectedHoliday"
              class="text-primary bg-primary-50 px-3 py-1 rounded-lg"
            >
              تعطیل
            </span>
          </div>

          <h3 class="text-blue-gray-500 text-xl font-weight-bold mb-3">
            یادآوری
          </h3>

          <VSkeletonLoader v-if="loading" type="list-item-two-line" />

          <div
            v-else-if="selectedDayReminders.length === 0"
            class="text-gray-400 text-sm"
          >
            {{ DASHBOARD_REMINDERS_EMPTY_MESSAGE }}
          </div>

          <div v-else class="d-flex flex-column">
            <div
              v-for="(reminder, index) in selectedDayReminders"
              :key="reminder.id"
              class="dashboard-calendar-card__reminder d-flex align-start ga-2"
            >
              <div class="flex-grow-1 min-w-0">
                <div class="text-gray-700">
                  <span
                    class="dashboard-calendar-card__dot rounded-circle d-inline-block bg-error mt-1 ms-2"
                    :style="{
                      backgroundColor:
                        reminderAccentColors[
                          index % reminderAccentColors.length
                        ],
                    }"
                  />
                  {{ reminder.title }}
                </div>
                <div class="text-gray-400 text-sm ps-4">
                  {{ reminder.time ?? '12:00' }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        class="dashboard-calendar-card__footer d-flex flex-column flex-sm-row align-sm-center justify-space-between ga-2 px-4 py-3 border-t"
      >
        <span class="text-gray-500 text-sm">
          {{ selectedDateFooterLabel }}
        </span>
        <button
          type="button"
          class="dashboard-calendar-card__back-today text-warning text-sm font-weight-medium border-0 bg-transparent pa-0"
          @click="goBackToToday"
        >
          {{ DASHBOARD_CALENDAR_BACK_TO_TODAY_LABEL }}
        </button>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue'
import { VSkeletonLoader } from 'vuetify/components'
import {
  DASHBOARD_CALENDAR_BACK_TO_TODAY_LABEL,
  DASHBOARD_CALENDAR_SECTION_TITLE,
  DASHBOARD_REMINDERS_EMPTY_MESSAGE,
} from '../DashboardPage.constants'
import type { Emits, Props } from './DashboardCalendarCard.types'
import ReminderCalendar from '@/modules/Reminder/components/ReminderCalendar/ReminderCalendar.vue'
import {
  findGregorianForJalali,
  formatDateKeyLabelWithWeekday,
  getJalaliParts,
  getTodayKey,
  toDateKey,
  toPersianDigits,
} from '@/modules/Reminder/utils/jalaliCalendar'

defineOptions({
  name: 'DashboardCalendarCard',
})

const props = withDefaults(defineProps<Props>(), {
  loading: false,
  mobile: false,
})

const emit = defineEmits<Emits>()

const reminderAccentColors = ['#EC4899', '#9333EA', '#2563EB', '#0D9488']

const selectedDateKey = ref<string | null>(getTodayKey())

const holidayDateKeysSet = computed(() => new Set(props.holidayDateKeys))

const effectiveSelectedDateKey = computed(
  () => selectedDateKey.value ?? getTodayKey()
)

const selectedDayReminders = computed(() =>
  props.reminders.filter(
    (reminder) => toDateKey(reminder.date) === effectiveSelectedDateKey.value
  )
)

const selectedParts = computed(() => {
  const [year, month, day] = effectiveSelectedDateKey.value
    .split('-')
    .map(Number)
  return { year, month, day }
})

const PERSIAN_WEEKDAYS = [
  'یکشنبه',
  'دوشنبه',
  'سه‌شنبه',
  'چهارشنبه',
  'پنجشنبه',
  'جمعه',
  'شنبه',
]

const PERSIAN_MONTHS = [
  'فروردین',
  'اردیبهشت',
  'خرداد',
  'تیر',
  'مرداد',
  'شهریور',
  'مهر',
  'آبان',
  'آذر',
  'دی',
  'بهمن',
  'اسفند',
]

const selectedGregorianDate = computed(() => {
  const { year, month, day } = selectedParts.value
  return findGregorianForJalali(year, month, day)
})

const selectedDateFooterLabel = computed(() =>
  formatDateKeyLabelWithWeekday(effectiveSelectedDateKey.value)
)

const selectedWeekdayLabel = computed(
  () => PERSIAN_WEEKDAYS[selectedGregorianDate.value.getDay()]
)

const selectedDayLabel = computed(() =>
  toPersianDigits(selectedParts.value.day)
)

const selectedMonthLabel = computed(
  () => PERSIAN_MONTHS[selectedParts.value.month - 1]
)

const isSelectedHoliday = computed(() =>
  holidayDateKeysSet.value.has(effectiveSelectedDateKey.value)
)

function onSelectedDateKeyChange(value: string | null) {
  selectedDateKey.value = value
  emit('update:selectedDateKey', value)
}

function goBackToToday() {
  const todayKey = getTodayKey()
  selectedDateKey.value = todayKey
  emit('update:selectedDateKey', todayKey)
  emit('update:viewMonth', getJalaliParts())
}
</script>

<style scoped lang="scss">
.dashboard-calendar-card__reminder {
  min-height: 48px;
  padding-block: 8px;
  border-bottom: 1px dashed rgb(var(--v-theme-gray-200));

  &:last-child {
    border-bottom: 0;
  }
}

.dashboard-calendar-card__dot {
  width: 4px;
  height: 4px;
}

.dashboard-calendar-card__back-today {
  cursor: pointer;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
}
</style>
